import { Stack, TextField, InputAdornment, Tooltip, useTheme } from "@mui/material";
import { DekoxIcon } from "src/components";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { configuratorPageK, useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { AddEditConfigurationDialog, AddEditLabel, ConfigurationsList, Header, OptionsPanel, ProductInformationDialog, Products, SelectedConfigurations, SelectedProducts } from "./components";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "./configurator-store";
import { ConfiguratorTranslationsProvider } from "./configurator-translations";
import { filterActions } from "./components/filter/filter-store";
import { viewSettingsActions } from "./components/view-settings/view-settings-store";
import { useEffect, useRef, useState, useTransition } from "react";
import { useOutlet, useParams } from "react-router";
import { IconButton } from "@itk/mui-components";

const mapStateToProps = (state: RootState) => ({
  activeTab: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.activeTab,
  addEditConfigurationDialog: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.addEditConfigurationDialog,
  isProductInformationDialogOpen: state.pageReducer.combinedConfiguratorReducer.productInformationDialogReducer.isProductInformationDialogOpen,
  selectedConfigurations: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedConfigurations,
  selectedProducts: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedProducts,
  searchTerm: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.searchTerm
});

const ownActions = {
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest,
  getLabelsRequest: configuratorActions.getLabelsRequest,
  getAttributesCatalogRequest: configuratorActions.getAttributesCatalogRequest,
  loadPersistedRequest: viewSettingsActions.loadPersistedRequest,
  clear: configuratorActions.clear,
  clearFilter: filterActions.clear, // filterReducer has its own Clear action type; must be dispatched separately
  clearViewSettings: viewSettingsActions.clear, // viewSettingsReducer has its own Clear action type; must be dispatched separately
  setSearchTerm: configuratorActions.setSearchTerm,
  setGroupFields: configuratorActions.setGroupFields,
  triggerExpandCollapse: configuratorActions.triggerExpandCollapse
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ConfiguratorBase = (props: Props) => {
  const {
    activeTab,
    addEditConfigurationDialog,
    isProductInformationDialogOpen,
    selectedConfigurations,
    selectedProducts,
    getConfigurationsRequest,
    getLabelsRequest,
    getAttributesCatalogRequest,
    loadPersistedRequest,
    clear,
    clearFilter,
    clearViewSettings,
    searchTerm,
    setSearchTerm,
    setGroupFields,
    triggerExpandCollapse
  } = props;

  // contentTab drives which panel is visible. It lags one render behind activeTab so
  // that the Header's tab indicator (reading real activeTab) can paint first, and only
  // then does React swap the content as a low-priority transition.
  const [, startTransition] = useTransition();
  const [contentTab, setContentTab] = useState(activeTab);
  useEffect(() => {
    startTransition(() => setContentTab(activeTab));
  }, [activeTab]);

  // Local search state with debounce: only dispatch to Redux after 200ms pause to avoid
  // expensive filter+sort+group recomputation on every keystroke.
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const searchDebounce = useRef<ReturnType<typeof setTimeout>>();

  const theme = useTheme();
  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const { projectId = "" } = useParams<{ projectId: string }>();

  // Configure Product renders as an overlay child route. The Configurator stays mounted
  // underneath, preserving scroll position and accordion state for free.
  const outlet = useOutlet();

  // Refetch the list when the Configure Product overlay closes so edits are reflected.
  const prevHadOutlet = useRef(false);
  useEffect(() => {
    if (prevHadOutlet.current && !outlet) {
      getConfigurationsRequest(projectId);
    }
    prevHadOutlet.current = !!outlet;
  }, [outlet]);

  useEffect(() => {
    loadPersistedRequest(projectId);
    getLabelsRequest(projectId);
    getAttributesCatalogRequest(projectId);

    return () => {
      clear();
      clearFilter(); // filterReducer uses a different Clear action type — must clear explicitly
      clearViewSettings(); // viewSettingsReducer uses a different Clear action type — must clear explicitly
    };
  }, []);

  // Only sync when searchTerm is externally cleared (e.g. clear() action resets to "").
  // Syncing on every debounced dispatch would set the same value, triggering an extra
  // reconciliation pass on Configurator after each keystroke.
  useEffect(() => {
    if (searchTerm === "") setLocalSearch("");
  }, [searchTerm]);

  // Clear any in-flight debounce on unmount
  useEffect(() => {
    return () => clearTimeout(searchDebounce.current);
  }, []);

  // the two tabs' group-by option sets don't overlap, so a selection valid on one tab would
  // silently be a no-op on the other if it persisted across the switch
  useEffect(() => {
    setGroupFields([]);
  }, [activeTab]);

  return (
    <ConfiguratorTranslationsProvider>
      <Stack flex={1} overflow="hidden" position="relative">
        <Stack flex={1} overflow="auto">
          <Header />
          <Stack direction="row" flex={1} overflow="auto">
            <Stack flex={1} overflow="auto">
              <Stack px={5} py={2} flex={1} minWidth={0} overflow="hidden" position="relative">
                {/* alignItems flex-start keeps the search box and expand/collapse buttons pinned
                  to the top */}
                <Stack direction="row" gap={1} mb={2} flexShrink={0} alignItems="flex-start">
                  <TextField
                    size="small"
                    sx={{ width: 300 }}
                    value={localSearch}
                    onChange={e => {
                      setLocalSearch(e.target.value);
                      clearTimeout(searchDebounce.current);
                      searchDebounce.current = setTimeout(() => setSearchTerm(e.target.value), 200);
                    }}
                    placeholder={genericTranslationsT.search}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <DekoxIcon icon="search" fontSize="small" />
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                  <Stack flex={1} />
                  {/* affects whichever grouped tree is currently visible (Products or Configurations) */}
                  <Stack direction="row" gap={1}>
                    <Tooltip title={configuratorPageT.expandAllGroups}>
                      <IconButton
                        k={configuratorPageK.expandAllGroups}
                        size="small"
                        onClick={() => triggerExpandCollapse("expand")}
                        sx={{ backgroundColor: theme.palette.customColors.white, border: `1px solid ${theme.palette.customColors.grey}`, borderRadius: 1 }}
                      >
                        <DekoxIcon icon="fullScreen" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={configuratorPageT.collapseAllGroups}>
                      <IconButton
                        k={configuratorPageK.collapseAllGroups}
                        size="small"
                        onClick={() => triggerExpandCollapse("collapse")}
                        sx={{ backgroundColor: theme.palette.customColors.white, border: `1px solid ${theme.palette.customColors.grey}`, borderRadius: 1 }}
                      >
                        <DekoxIcon icon="fullScreenExit" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {/* Only the active tab's list is mounted. Keeping both mounted meant the hidden tab's
                  rows (every product line item across every configuration) still rendered, laid out
                  and re-rendered on every dispatch — on a 500-configuration project that doubled the
                  cost of every interaction. The wrappers stay so the fade still plays; contentTab
                  lags one render behind activeTab so the Header indicator paints first. */}
                <Stack
                  sx={{
                    flex: contentTab === "CONFIGURATIONS" ? "1 1 0%" : "0 0 0px",
                    minHeight: 0,
                    overflow: "hidden",
                    opacity: contentTab === "CONFIGURATIONS" ? 1 : 0,
                    transform: contentTab === "CONFIGURATIONS" ? "translateY(0)" : "translateY(8px)",
                    transition: "opacity 0.18s ease, transform 0.18s ease",
                    pointerEvents: contentTab === "CONFIGURATIONS" ? "auto" : "none"
                  }}
                >
                  {contentTab === "CONFIGURATIONS" && <ConfigurationsList />}
                </Stack>
                <Stack
                  sx={{
                    flex: contentTab === "PRODUCTS" ? "1 1 0%" : "0 0 0px",
                    minHeight: 0,
                    overflow: "hidden",
                    opacity: contentTab === "PRODUCTS" ? 1 : 0,
                    transform: contentTab === "PRODUCTS" ? "translateY(0)" : "translateY(8px)",
                    transition: "opacity 0.18s ease, transform 0.18s ease",
                    pointerEvents: contentTab === "PRODUCTS" ? "auto" : "none"
                  }}
                >
                  {contentTab === "PRODUCTS" && <Products />}
                </Stack>
                <AddEditLabel />
              </Stack>
              {/* Each tab owns its own bar — the selections target different entities */}
              {activeTab === "CONFIGURATIONS" && selectedConfigurations.length > 0 && <SelectedConfigurations />}
              {activeTab === "PRODUCTS" && selectedProducts.length > 0 && <SelectedProducts />}
            </Stack>
            <OptionsPanel />
          </Stack>
          {addEditConfigurationDialog && <AddEditConfigurationDialog />}
          {isProductInformationDialogOpen && <ProductInformationDialog />}
        </Stack>
        {outlet && (
          <Stack position="absolute" zIndex={1} sx={{ inset: 0, bgcolor: "customColors.lightGrey" }}>
            {outlet}
          </Stack>
        )}
      </Stack>
    </ConfiguratorTranslationsProvider>
  );
};

export const Configurator = connect(mapStateToProps, ownActions)(ConfiguratorBase);
